import axios from 'axios';
import { API_BASE_URL } from './api-config';
import {
  ensureValidAccessToken,
  getAccessToken,
  isIdleTimeoutExceeded,
  logoutToLogin,
  recordUserActivity,
  refreshAccessToken,
} from '../utils/session-utils';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const isLoginAttempt = (config) => {
  const u = config?.url || '';
  return u.includes('auth/authenticate');
};

const isRefreshAttempt = (config) => {
  const u = config?.url || '';
  return u.includes('auth/refresh');
};

axiosInstance.interceptors.request.use(
  async (config) => {
    if (isLoginAttempt(config) || isRefreshAttempt(config)) {
      return config;
    }

    const token = getAccessToken();

    if (token) {
      if (isIdleTimeoutExceeded()) {
        logoutToLogin({
          message: 'You have been logged out due to 15 minutes of inactivity.',
        });
        return Promise.reject(new axios.Cancel('Session expired due to inactivity'));
      }

      try {
        const validToken = await ensureValidAccessToken();
        recordUserActivity();
        config.headers.Authorization = `Bearer ${validToken}`;
      } catch (error) {
        if (!axios.isCancel(error)) {
          logoutToLogin();
        }
        return Promise.reject(error);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isLoginAttempt(originalRequest) &&
      !isRefreshAttempt(originalRequest)
    ) {
      if (isIdleTimeoutExceeded()) {
        logoutToLogin({
          message: 'You have been logged out due to 15 minutes of inactivity.',
        });
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        recordUserActivity();
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        logoutToLogin();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
